import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, RefreshCw, Timer, School as SchoolIcon, Copy, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Teacher-side QR generator — mints a rotating attendance code every {ttl} seconds
 * and renders it as a big QR + human-readable digits so students in the back rows
 * can just type it.
 */
export default function SchoolTeacherQR() {
  const { user } = useAuth();
  const [schoolId, setSchoolId] = useState("");
  const [className, setClassName] = useState("");
  const [ttl, setTtl] = useState(90);
  const [code, setCode] = useState(null);           // {code, expires_at, qr_payload}
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const tickerRef = useRef(null);

  const mint = async () => {
    if (!schoolId.trim() || !className.trim()) { toast.error("School ID + class required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/school/attendance/code", {
        school_id: schoolId.trim(), class_name: className.trim(), ttl_secs: ttl,
      });
      setCode(r.data);
      setCopied(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  // Live countdown
  useEffect(() => {
    if (!code?.expires_at) { setSecondsLeft(0); return; }
    const tick = () => {
      const left = Math.max(0, Math.round((new Date(code.expires_at).getTime() - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0 && tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
    };
    tick();
    tickerRef.current = setInterval(tick, 1000);
    return () => { if (tickerRef.current) clearInterval(tickerRef.current); };
  }, [code?.expires_at]);

  const copy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!user) return <div className="p-12 text-center text-stone-500">Sign in as a teacher/admin to mint codes.</div>;

  return (
    <div className="max-w-xl mx-auto px-4 py-4" data-testid="school-teacher-qr">
      <div className="flex items-center gap-2 mb-1">
        <SchoolIcon className="text-indigo-500" size={22} />
        <h1 className="heading text-2xl font-black text-stone-900">Attendance QR</h1>
      </div>
      <p className="text-xs text-stone-500 mb-4">Project this page in class. Students scan the QR or type the 6-digit code.</p>

      <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-3">
        <Input placeholder="School ID (sch_…)" value={schoolId} onChange={e => setSchoolId(e.target.value)} data-testid="teacher-school-id" />
        <Input placeholder="Class name (e.g. 8-A)" value={className} onChange={e => setClassName(e.target.value)} data-testid="teacher-class-name" />
        <div className="flex items-center gap-2">
          <label className="text-xs text-stone-500 whitespace-nowrap">TTL sec</label>
          <input type="number" min="10" max="300" value={ttl} onChange={e => setTtl(+e.target.value)} className="border border-stone-300 rounded-lg px-3 py-2 text-sm w-24" data-testid="teacher-ttl" />
        </div>
        <Button onClick={mint} disabled={busy} className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="teacher-mint-btn">
          {busy ? <Loader2 className="animate-spin" size={14} /> : (code ? <><RefreshCw size={14} className="mr-1.5" /> Rotate code</> : "Generate code")}
        </Button>
      </div>

      {code && (
        <div className={`mt-4 rounded-3xl p-6 text-center shadow-2xl ${secondsLeft === 0 ? "bg-stone-200 text-stone-500" : "bg-gradient-to-br from-indigo-600 to-violet-800 text-white"}`}>
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-80 mb-2">
            {secondsLeft === 0 ? "Code expired — rotate" : "Live attendance code"}
          </div>
          <div className="inline-block bg-white p-3 rounded-2xl">
            <QRCodeSVG value={code.qr_payload || `find2near://school/attend?code=${code.code}`} size={200} level="M" />
          </div>
          <div className="mt-4 flex items-center justify-center gap-2">
            <div className="text-5xl md:text-6xl font-black tracking-[0.4em] font-mono" data-testid="teacher-code-display">
              {code.code}
            </div>
            <button onClick={copy} className="p-2 rounded-full hover:bg-white/15" data-testid="teacher-copy-btn">
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
          <div className="mt-2 text-xs inline-flex items-center gap-1 opacity-80">
            <Timer size={12} /> {secondsLeft > 0 ? `${secondsLeft}s remaining` : "Please rotate"}
          </div>
        </div>
      )}

      <Link to="/school" className="mt-4 block text-center text-xs font-bold text-[#E05E36] hover:underline">← Student dashboard</Link>
    </div>
  );
}
